<template>
  <div class="ue-page">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Upcoming Events</h1>
        <p class="subtitle">Earnings releases and key dates for the next 5 weeks</p>
      </div>
      <div class="header-actions">
        <SubsectorFilter
          :available-subsectors="availableSubsectors"
          :excluded-subsectors="excludedSubsectors"
          @toggle="toggleSubsector"
          @show-all="showAllSubsectors"
          @hide-all="hideAllSubsectors"
        />
        <button class="btn-refresh" :disabled="loading" @click="loadEvents">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Loading…' : '↺ Refresh' }}
        </button>
      </div>
    </div>

    <div v-if="error" class="error-box">{{ error }}</div>

    <!-- Three-panel layout -->
    <div class="panels">

      <!-- ── Panel 1: All Events ───────────────────────────────────────── -->
      <div class="panel">
        <div class="panel-header">
          <span class="panel-title">All Events</span>
          <span class="badge">{{ visibleEvents.length }}</span>
        </div>

        <div v-if="loading && events.length === 0" class="panel-empty">
          <span class="spinner spinner-dark"></span>
        </div>

        <div v-else-if="!loading && events.length === 0" class="panel-empty">
          No events found.
        </div>

        <div v-else class="event-list">
          <div v-for="(group, date) in groupedEvents" :key="date">
            <div class="date-divider">{{ formatDateHeader(date) }}</div>
            <div
              v-for="ev in group"
              :key="ev.eventid"
              class="event-card"
              :class="{ 'is-checked': ev.checked_event_date }"
            >
              <div class="event-card-top">
                <span class="ev-ric-group">
                  <span class="ev-ric">{{ ev.ric }}</span>
                  <span v-if="ev.company_name" class="ev-company">{{ ev.company_name }}</span>
                </span>
                <span class="ev-type-badge">{{ shortType(ev.eventtype) }}</span>
              </div>
              <div class="ev-description">{{ ev.eventdescription }}</div>
              <div class="event-card-bottom">
                <span class="ev-date-group">
                  <span class="ev-date">{{ ev.eventdate }}</span>
                  <span class="ev-subsector">{{ ev.gics_subsector || '—' }}</span>
                </span>
                <span v-if="ev.checked_event_date" class="ev-checked">
                  ✓ Checked {{ ev.checked_event_date }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Panel 2: Analyzed Events ─────────────────────────────────── -->
      <div class="panel">
        <div class="panel-header">
          <span class="panel-title">Analyzed Events</span>
          <span class="badge">{{ visibleAnalyzedEvents.length }}</span>
        </div>

        <div v-if="loadingAnalyzed && analyzedEvents.length === 0" class="panel-empty">
          <span class="spinner spinner-dark"></span>
        </div>

        <div v-else-if="!loadingAnalyzed && analyzedEvents.length === 0" class="panel-empty">
          No analyzed events found.
        </div>

        <div v-else class="event-list">
          <div v-for="(group, date) in groupedAnalyzedEvents" :key="date">
            <div class="date-divider">{{ formatDateHeader(date) }}</div>
            <div
              v-for="ev in group"
              :key="ev.eventid"
              class="event-card event-card--analyzed"
              title="Double-click to open framework"
              @dblclick="openFramework(ev.ric)"
            >
              <div class="event-card-top">
                <span class="ev-ric-group">
                  <span class="ev-ric">{{ ev.ric }}</span>
                  <span v-if="ev.company_name" class="ev-company">{{ ev.company_name }}</span>
                </span>
                <span class="ev-type-badge">{{ shortType(ev.eventtype) }}</span>
              </div>
              <div class="ev-description">{{ ev.eventdescription }}</div>
              <div v-if="ev.framework" class="ev-framework-row">
                <span v-if="ev.framework.mifp_period" class="fw-tag">MIFP {{ ev.framework.mifp_period }}</span>
                <span
                  v-if="ev.framework.upside_pct != null"
                  class="fw-tag"
                  :class="ev.framework.upside_pct >= 0 ? 'fw-pos' : 'fw-neg'"
                >{{ ev.framework.upside_pct >= 0 ? '+' : '' }}{{ ev.framework.upside_pct.toFixed(1) }}%</span>
                <span v-if="ev.framework.implied_roic != null" class="fw-tag">ROIC {{ ev.framework.implied_roic.toFixed(1) }}%</span>
                <span v-if="ev.framework.wacc != null" class="fw-tag">WACC {{ ev.framework.wacc.toFixed(1) }}%</span>
              </div>
              <div class="event-card-bottom">
                <span class="ev-date-group">
                  <span class="ev-date">{{ ev.eventdate }}</span>
                  <span class="ev-subsector">{{ ev.gics_subsector || '—' }}</span>
                </span>
                <span v-if="ev.framework?.run_at" class="ev-analyzed">
                  ✓ {{ formatRunAt(ev.framework.run_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Panel 3: Watchlist ────────────────────────────────────────── -->
      <div class="panel">
        <div class="panel-header">
          <span class="panel-title">Watchlist</span>
          <span class="badge">{{ visibleWatchlistEvents.length }}</span>
        </div>

        <div v-if="loadingWatchlist && watchlistEvents.length === 0" class="panel-empty">
          <span class="spinner spinner-dark"></span>
        </div>

        <div v-else-if="!loadingWatchlist && watchlistEvents.length === 0" class="panel-empty">
          No scored candidates found.
        </div>

        <div v-else class="event-list">
          <!-- Active tier -->
          <template v-if="watchlistByTier.active.length">
            <div class="date-divider tier-active">Active · ≤ 21 days</div>
            <div
              v-for="ev in watchlistByTier.active"
              :key="ev.ric + ev.eventdate"
              class="event-card event-card--watchlist"
              title="Doppelklick öffnet den Research-Prozess"
              @dblclick="openResearch(ev)"
            >
              <div class="event-card-top">
                <span class="ev-ric-group">
                  <span class="ev-ric">{{ ev.ric }}</span>
                  <span v-if="ev.company_name" class="ev-company">{{ ev.company_name }}</span>
                </span>
                <span class="wl-days" :class="ev.days_until <= 7 ? 'days-urgent' : 'days-soon'">
                  {{ ev.days_until }}d
                </span>
              </div>

              <div class="ev-framework-row" v-if="ev.framework">
                <span v-if="ev.framework.mifp_period" class="fw-tag">MIFP {{ ev.framework.mifp_period }}</span>
                <span
                  v-if="ev.framework.upside_pct != null"
                  class="fw-tag"
                  :class="ev.framework.upside_pct >= 0 ? 'fw-pos' : 'fw-neg'"
                >{{ ev.framework.upside_pct >= 0 ? '+' : '' }}{{ ev.framework.upside_pct.toFixed(1) }}%</span>
                <span v-if="ev.framework.implied_roic != null" class="fw-tag">ROIC {{ ev.framework.implied_roic.toFixed(1) }}%</span>
                <span v-if="ev.framework.wacc != null" class="fw-tag">WACC {{ (ev.framework.wacc * 100).toFixed(1) }}%</span>
              </div>

              <div v-if="ev.scoring" class="wl-scoring-row">
                <span class="wl-dots">
                  <span
                    v-for="i in 5" :key="i"
                    class="wl-dot"
                    :class="i <= ev.scoring.expectation_level ? 'dot-on' : 'dot-off'"
                  ></span>
                </span>
                <span class="wl-score-num">{{ ev.scoring.composite_score.toFixed(1) }}</span>
                <span class="wl-signal" :class="wlSignalClass(ev.scoring.signal)">{{ ev.scoring.signal }}</span>
              </div>

              <div v-if="ev.watchlist_flags && ev.watchlist_flags.length" class="wl-flags">
                <span v-for="flag in ev.watchlist_flags" :key="flag" class="wl-flag">{{ flag }}</span>
              </div>

              <div class="event-card-bottom">
                <span class="ev-date-group">
                  <span class="ev-date">{{ ev.eventdate }}</span>
                  <span class="ev-subsector">{{ ev.gics_subsector || '—' }}</span>
                </span>
                <span class="wl-actions">
                  <span v-if="ev.scoring?.key_driver" class="wl-driver">Key: {{ ev.scoring.key_driver }}</span>
                  <button
                    type="button"
                    class="wl-research-btn"
                    title="Research-Prozess öffnen"
                    @click.stop="openResearch(ev)"
                  >
                    Research →
                  </button>
                </span>
              </div>
            </div>
          </template>

          <!-- Observe tier -->
          <template v-if="watchlistByTier.observe.length">
            <div class="date-divider tier-observe">Observe · 22–35 days</div>
            <div
              v-for="ev in watchlistByTier.observe"
              :key="ev.ric + ev.eventdate"
              class="event-card event-card--watchlist"
              title="Doppelklick öffnet den Research-Prozess"
              @dblclick="openResearch(ev)"
            >
              <div class="event-card-top">
                <span class="ev-ric-group">
                  <span class="ev-ric">{{ ev.ric }}</span>
                  <span v-if="ev.company_name" class="ev-company">{{ ev.company_name }}</span>
                </span>
                <span class="wl-days days-observe">{{ ev.days_until }}d</span>
              </div>

              <div class="ev-framework-row" v-if="ev.framework">
                <span v-if="ev.framework.mifp_period" class="fw-tag">MIFP {{ ev.framework.mifp_period }}</span>
                <span
                  v-if="ev.framework.upside_pct != null"
                  class="fw-tag"
                  :class="ev.framework.upside_pct >= 0 ? 'fw-pos' : 'fw-neg'"
                >{{ ev.framework.upside_pct >= 0 ? '+' : '' }}{{ ev.framework.upside_pct.toFixed(1) }}%</span>
                <span v-if="ev.framework.implied_roic != null" class="fw-tag">ROIC {{ ev.framework.implied_roic.toFixed(1) }}%</span>
                <span v-if="ev.framework.wacc != null" class="fw-tag">WACC {{ (ev.framework.wacc * 100).toFixed(1) }}%</span>
              </div>

              <div v-if="ev.scoring" class="wl-scoring-row">
                <span class="wl-dots">
                  <span
                    v-for="i in 5" :key="i"
                    class="wl-dot"
                    :class="i <= ev.scoring.expectation_level ? 'dot-on' : 'dot-off'"
                  ></span>
                </span>
                <span class="wl-score-num">{{ ev.scoring.composite_score.toFixed(1) }}</span>
                <span class="wl-signal" :class="wlSignalClass(ev.scoring.signal)">{{ ev.scoring.signal }}</span>
              </div>

              <div v-if="ev.watchlist_flags && ev.watchlist_flags.length" class="wl-flags">
                <span v-for="flag in ev.watchlist_flags" :key="flag" class="wl-flag">{{ flag }}</span>
              </div>

              <div class="event-card-bottom">
                <span class="ev-date-group">
                  <span class="ev-date">{{ ev.eventdate }}</span>
                  <span class="ev-subsector">{{ ev.gics_subsector || '—' }}</span>
                </span>
                <span class="wl-actions">
                  <span v-if="ev.scoring?.key_driver" class="wl-driver">Key: {{ ev.scoring.key_driver }}</span>
                  <button
                    type="button"
                    class="wl-research-btn"
                    title="Research-Prozess öffnen"
                    @click.stop="openResearch(ev)"
                  >
                    Research →
                  </button>
                </span>
              </div>
            </div>
          </template>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { fetchEvents, fetchAnalyzedEvents, fetchWatchlist } from '@/services/api'
import SubsectorFilter from '@/components/SubsectorFilter.vue'

export default {
  name: 'UpcomingEvents',

  components: { SubsectorFilter },

  data() {
    return {
      events:             [],
      loading:            false,
      error:              null,
      analyzedEvents:     [],
      loadingAnalyzed:    false,
      watchlistEvents:    [],
      loadingWatchlist:   false,
      excludedSubsectors: [],
    }
  },

  computed: {
    availableSubsectors() {
      const all = [...this.events, ...this.analyzedEvents, ...this.watchlistEvents]
      return [...new Set(all.map(e => e.gics_subsector).filter(Boolean))].sort()
    },
    visibleEvents() {
      return this._visible(this.events)
    },
    visibleAnalyzedEvents() {
      return this._visible(this.analyzedEvents)
    },
    visibleWatchlistEvents() {
      return this._visible(this.watchlistEvents)
    },
    groupedEvents() {
      return this._group(this.visibleEvents)
    },
    groupedAnalyzedEvents() {
      return this._group(this.visibleAnalyzedEvents)
    },
    watchlistByTier() {
      const active  = this.visibleWatchlistEvents.filter(e => e.watchlist_tier === 'active')
      const observe = this.visibleWatchlistEvents.filter(e => e.watchlist_tier === 'observe')
      const byDays  = (a, b) => a.days_until - b.days_until
      return { active: active.sort(byDays), observe: observe.sort(byDays) }
    },
  },

  mounted() {
    this.loadEvents()
    this.loadAnalyzedEvents()
    this.loadWatchlist()
  },

  methods: {
    async loadEvents() {
      this.loading = true
      this.error   = null
      try {
        const { data } = await fetchEvents()
        this.events = data.events || []
      } catch (e) {
        this.error = e.response?.data?.error || e.message || 'Failed to load events'
      } finally {
        this.loading = false
      }
    },

    async loadAnalyzedEvents() {
      this.loadingAnalyzed = true
      try {
        const { data } = await fetchAnalyzedEvents()
        this.analyzedEvents = data.events || []
      } catch {
        // silently fail; panel shows empty state
      } finally {
        this.loadingAnalyzed = false
      }
    },

    async loadWatchlist() {
      this.loadingWatchlist = true
      try {
        const { data } = await fetchWatchlist()
        this.watchlistEvents = data.events || []
      } catch {
        // silently fail; panel shows empty state
      } finally {
        this.loadingWatchlist = false
      }
    },

    openFramework(ric) {
      this.$router.push({ name: 'ExpectationsFramework', query: { ric } })
    },

    openResearch(ev) {
      this.$router.push({ name: 'research-overview', params: { ric: ev.ric } })
    },

    wlSignalClass(signal) {
      if (!signal) return 'wl-signal-neutral'
      const s = signal.toLowerCase()
      if (s.includes('sehr interessant') || s.includes('stark')) return 'wl-signal-strong'
      if (s.includes('interessant')) return 'wl-signal-positive'
      if (s.includes('uninteressant') || s.includes('negativ') || s.includes('avoid')) return 'wl-signal-negative'
      return 'wl-signal-neutral'
    },

    toggleSubsector(subsector) {
      const i = this.excludedSubsectors.indexOf(subsector)
      if (i === -1) this.excludedSubsectors.push(subsector)
      else this.excludedSubsectors.splice(i, 1)
    },

    showAllSubsectors() {
      this.excludedSubsectors = []
    },

    hideAllSubsectors() {
      this.excludedSubsectors = [...this.availableSubsectors]
    },

    _visible(list) {
      return list.filter(e => !e.gics_subsector || !this.excludedSubsectors.includes(e.gics_subsector))
    },

    _group(list) {
      const groups = {}
      for (const ev of list) {
        if (!groups[ev.eventdate]) groups[ev.eventdate] = []
        groups[ev.eventdate].push(ev)
      }
      return groups
    },

    formatDateHeader(dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
    },

    formatRunAt(ts) {
      if (!ts) return ''
      return new Date(ts).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },

    shortType(type) {
      const map = {
        EarningsReleases: 'Earnings',
        EarningsAnnouncementDate: 'Announcement',
        Dividends: 'Dividend',
        StockSplits: 'Split',
      }
      return map[type] || type
    },
  },
}
</script>

<style scoped>
.ue-page {
  padding: 0;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #003366;
  margin-bottom: 4px;
}

.subtitle {
  font-size: 14px;
  color: #6b7280;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.error-box {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #b91c1c;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
}

/* Refresh button */
.btn-refresh {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: #003366;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s;
}
.btn-refresh:hover:not(:disabled) { background: #004499; }
.btn-refresh:disabled { opacity: 0.55; cursor: not-allowed; }

/* Three-panel grid */
.panels {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px;
  align-items: start;
}

.panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 480px;
  max-height: 78vh;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 700;
  color: #003366;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.badge {
  background: #003366;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  min-width: 24px;
  text-align: center;
}

/* Scrollable event list */
.event-list {
  overflow-y: auto;
  flex: 1;
  padding: 8px 0;
}

/* Date group header */
.date-divider {
  padding: 8px 18px 4px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  background: #f9fafb;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 1;
}

/* Individual event card */
.event-card {
  padding: 12px 18px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.1s;
}
.event-card:last-child { border-bottom: none; }
.event-card:hover { background: #f9fafb; }
.event-card.is-checked { opacity: 0.55; }

.event-card-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.ev-ric-group {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.ev-ric {
  font-size: 14px;
  font-weight: 700;
  color: #003366;
  font-family: 'Courier New', monospace;
  flex-shrink: 0;
}

.ev-company {
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ev-type-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  background: #dbeafe;
  color: #1d4ed8;
  white-space: nowrap;
  flex-shrink: 0;
}

.ev-description {
  font-size: 12px;
  color: #374151;
  line-height: 1.4;
  margin-bottom: 6px;
}

.event-card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ev-date-group {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.ev-date {
  font-size: 11px;
  color: #9ca3af;
  flex-shrink: 0;
}

.ev-subsector {
  font-size: 10px;
  font-weight: 600;
  color: #4da6ff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ev-checked {
  font-size: 11px;
  color: #16a34a;
  font-weight: 600;
}

/* Analyzed event cards */
.event-card--analyzed {
  cursor: pointer;
}
.event-card--analyzed:hover {
  background: #f0f6ff;
}

.ev-framework-row {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 6px;
}

.fw-tag {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #374151;
  white-space: nowrap;
}
.fw-pos { background: #dcfce7; color: #15803d; }
.fw-neg { background: #fee2e2; color: #b91c1c; }

.ev-analyzed {
  font-size: 11px;
  color: #6366f1;
  font-weight: 600;
}

/* Watchlist cards */
.event-card--watchlist {
  cursor: pointer;
}
.event-card--watchlist:hover {
  background: #fffbeb;
}

.wl-days {
  font-size: 11px; font-weight: 700; padding: 2px 8px;
  border-radius: 10px; white-space: nowrap; flex-shrink: 0;
}
.days-urgent { background: #fee2e2; color: #b91c1c; }
.days-soon   { background: #fef3c7; color: #92400e; }
.days-observe{ background: #f3f4f6; color: #6b7280; }

.wl-scoring-row {
  display: flex; align-items: center; gap: 6px; margin-bottom: 5px;
}
.wl-dots { display: flex; gap: 3px; }
.wl-dot {
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
}
.wl-dot.dot-on  { background: #003366; }
.wl-dot.dot-off { background: #d1d5db; }
.wl-score-num {
  font-size: 12px; font-weight: 700; color: #374151;
}
.wl-signal {
  font-size: 10px; font-weight: 700; padding: 1px 7px;
  border-radius: 10px; white-space: nowrap;
}
.wl-signal-strong   { background: #dcfce7; color: #15803d; }
.wl-signal-positive { background: #dbeafe; color: #1d4ed8; }
.wl-signal-neutral  { background: #f3f4f6; color: #6b7280; }
.wl-signal-negative { background: #fee2e2; color: #b91c1c; }

.wl-flags {
  display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 5px;
}
.wl-flag {
  font-size: 10px; font-weight: 600; padding: 1px 7px;
  border-radius: 10px; background: #fef3c7; color: #92400e;
  border: 1px solid #fde68a;
}

.wl-driver {
  font-size: 11px; color: #6b7280; font-weight: 600;
}

.wl-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.wl-research-btn {
  font-size: 10px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 10px;
  background: #fff;
  color: #003366;
  border: 1px solid #4da6ff;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s, color 0.15s;
}
.wl-research-btn:hover {
  background: #003366;
  color: #fff;
}

.tier-active  { color: #b45309; }
.tier-observe { color: #4b5563; }

/* Empty / coming-soon states */
.panel-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #9ca3af;
  font-size: 14px;
  padding: 40px 20px;
}

.coming-soon {
  color: #d1d5db;
}

.cs-icon {
  font-size: 28px;
  opacity: 0.35;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

.spinner-dark {
  border-color: rgba(0, 51, 102, 0.2);
  border-top-color: #003366;
  width: 24px;
  height: 24px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
